import Icon from "@/Components/Icon/Icon";
import useLayoutData from "@/shared/hooks/useLayoutData";
import { cn } from "@/shared/utils/cn";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";

/**
 * When provided, every TextfieldIntControlled below it follows this language
 * instead of rendering its own per-input language switcher.
 */
export const LanguageTabContext = createContext<string | null>(null);

export const useLanguageTab = () => useContext(LanguageTabContext);

export const useTabLanguages = () => {
  const { franchiseLanguages } = useLayoutData();
  return franchiseLanguages.filter((l) => l.key !== "turkish");
};

interface Props {
  selected: string;
  // Defaults to the franchise languages
  languages?: ReturnType<typeof useTabLanguages>;
  onChange: (key: string) => void;
  className?: string;
}

const LanguageTabs = ({
  selected,
  languages: languagesProp,
  onChange,
  className,
}: Props) => {
  const { t } = useTranslation();
  const tabLanguages = useTabLanguages();
  const languages = languagesProp ?? tabLanguages;

  if (languages.length <= 1) return null;

  return (
    <div
      role="tablist"
      className={cn(
        // Wrap instead of scrolling so every franchise language stays visible.
        "flex flex-wrap items-center gap-2",
        className
      )}
    >
      {languages.map((language) => {
        const isActive = language.key === selected;
        return (
          <button
            key={language.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(language.key)}
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-full border px-4 py-2 text-small font-semibold whitespace-nowrap transition-colors",
              isActive
                ? "border-primary bg-light text-primary"
                : "border-border bg-white text-text-secondary hover:text-text"
            )}
          >
            <Icon
              className={cn(
                "h-8 w-8 shrink-0",
                !language.icon &&
                  (isActive ? "fill-primary" : "fill-text-secondary")
              )}
              variants={language.icon ?? "th"}
            />
            {t(language.label)}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageTabs;
